import { effect, inject, Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { JhmTableRepository } from "@shared/src/lib/components/jhm-table/jhm-table.component";
import { CentsToDollarsPipe, ScreenStore, SecondsToDateRenderer, UserStore } from "@shared/src/public-api";
import { ColDef, ICellRendererParams } from "ag-grid-community";

export class UserCreditHistory
{
	public id: string;
	public userId: string;
	public creditAmount: number;
	public messageId: string;
	public changePurchaseTransactionId: string;
	public nmateUserId: string;
	public inmateFirstName: string;
	public inmateLastName: string;
	public inmateUsername: string;
	public type: string;
	public instantSeconds: number;
	public creditCost: number;
	public taxCost: number;
	public totalCost: number;
}

@Injectable()
export class UserCreditHistoryRepository implements OnDestroy, JhmTableRepository
{
	private requestMapping: string = "credits";
	private http: HttpClient;
	public inboxOrSent: string | undefined | null;
	public userId: string | undefined | null;
	public ready: BehaviorSubject<boolean>;
	public screenSize: string;
	public isMobile: boolean = false;
	public ngDestroyed$: Subject<any> = new Subject();
	private centsToDollarsPipe: CentsToDollarsPipe;

	public colDefs: ColDef[];

	public normalColDefs: ColDef[] = [
		{headerName: "", field: "typeIcon", cellRenderer: this.typeIconFunction, maxWidth: 50},
		{headerName: "Type", field: "type", cellRenderer: this.typeNameFunction},
		{headerName: "Credits", field: "creditAmount", cellRenderer: ((params: ICellRendererParams) => {
			return this.centsToDollarsPipe.transform(params.data.creditAmount)
		}) },
		{headerName: "Cost", field: "totalCost"},
		{headerName: "Date", field: "instantSeconds", cellRenderer: SecondsToDateRenderer}
	];

	public mobileColDefs: ColDef[] = [
		{headerName: "", field: "typeIcon", cellRenderer: this.typeIconFunction},
		{headerName: "Type", field: "type", cellRenderer: this.typeNameFunction},
		{headerName: "Credits", field: "creditAmount"},
		{headerName: "Date", field: "instantSeconds", cellRenderer: SecondsToDateRenderer}
	];

	readonly userStore = inject(UserStore);
	readonly screenStore = inject(ScreenStore);

	public constructor(http: HttpClient, centsToDollarsPipe: CentsToDollarsPipe)
	{
		this.centsToDollarsPipe = centsToDollarsPipe;
		this.http = http;
		this.ready = new BehaviorSubject<boolean>(false);

		effect(() => {
			this.userId = this.userStore.userId();
			if (this.userId)
			{
				this.colDefs = this.isMobile ? this.mobileColDefs : this.normalColDefs;
				this.tableReady();
			}
		})

		effect(() => {
			this.screenSize = this.screenStore.screenSize();
			if (this.screenSize === "MOBILE") {
				this.isMobile = true;
			}
			else {
				this.isMobile = false;
			}
		})
	}

	tableReady(): void {
		if (this.userId && this.colDefs) {
			this.ready.next(true);
		}
	}

	ngOnDestroy(): void
	{
		this.ngDestroyed$.next(true);
	}

	public typeIconFunction(params: ICellRendererParams): string
	{
		if (params.data.type === "SEND_MESSAGE")
		{
			return "<i class=\"fa-light fa-envelope\"></i>";
		}
		else if (params.data.type === "PAY_COLLECT_MESSAGE")
		{
			return "<i class=\"fa-light fa-receipt\"></i>"
		}
		else if (params.data.type === "INMATE_GIFT")
		{
			return "<i class=\"fa-light fa-arrow-right-from-arc\"></i>"
		}
		else if (params.data.type === "PURCHASE")
		{
			return "<i class=\"fa-light fa-cart-circle-check\"></i>"
		}
		else if (params.data.type === "REFUND")
		{
			return "<i class=\"fa-light fa-hand-holding-dollar\"></i>"
		}
		else
		{
			return "<i class=\"fa-light fa-cart-circle-check\"></i>"
		}
	}

	public typeNameFunction(params: ICellRendererParams): string
	{
		if (params.data.type === "SEND_MESSAGE")
		{
			return "Message Sent";
		}
		else if (params.data.type === "PAY_COLLECT_MESSAGE")
		{
			return "Collect Message Purchased";
		}
		else if (params.data.type === "INMATE_GIFT")
		{
			return "Gift To Inmate";
		}
		else if (params.data.type === "PURCHASE")
		{
			return "Purchased";
		}
		else if (params.data.type === "REFUND")
		{
			return "Refunded";
		}
		else
		{
			return "";
		}
	}

	public fetchAll(): Observable<UserCreditHistory[]>
	{
		return this.http.get<UserCreditHistory[]>(`${this.requestMapping}/users/${this.userId}/history`);
	}
}
